// Shared redemption UI — slice 4a-5.
//
// Two pieces, reusable across owner + staff trees:
//   <RedeemableMilestonesList>  — picker of earned-unredeemed milestones
//   <RedemptionConfirmModal>    — confirm + POST + result panel
//
// The HTTP caller is injected via `postRedemption` so the same component
// can use `ownerApi` in the owner tree and the staff API client in the
// staff tree without crossing a client boundary.

import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { newId } from '@naya/shared'

import { SlideToConfirm } from '../SlideToConfirm.js'

export interface Milestone {
  at_stamp: number
  reward_text: string
  type: 'intermediate' | 'final'
}

// Secret menu — mirrors GET /cards/:cardId's `secret_menu_context`. Non-null
// only when the card has a pending FINAL milestone (see apps/api/src/routes/
// cards.ts). `eligible: true` means the card banked enough off-menu stamps
// this cycle to unlock the secret reward alongside the regular grand prize.
export interface SecretMenuContext {
  eligible: boolean
  reward_text: string | null
  secret_stamp_count: number
  required_secret_stamps: number | null
}

export interface RedeemableMilestonesListProps {
  milestones: Milestone[]
  onRedeem: (milestone: Milestone) => void
  disabled?: boolean
  /** Secret menu — omit/null when the card has no pending final milestone or
   *  the feature isn't in play. Drives a small "N/M off-menu stamps banked"
   *  hint on the grand-prize row while the card hasn't qualified yet. */
  secretMenuContext?: SecretMenuContext | null
}

export function RedeemableMilestonesList({
  milestones,
  onRedeem,
  disabled,
  secretMenuContext,
}: RedeemableMilestonesListProps) {
  if (milestones.length === 0) return null
  // Small, unobtrusive progress hint — only when the card has banked at
  // least one secret stamp this cycle but hasn't hit the threshold yet.
  const showSecretProgress =
    !!secretMenuContext &&
    !secretMenuContext.eligible &&
    secretMenuContext.secret_stamp_count > 0
  // Once eligible, tell staff right here — don't make them tap Redeem to
  // discover a choice exists. The choice itself still happens one step
  // later in RedemptionConfirmModal; this is just the heads-up.
  const showSecretUnlocked = !!secretMenuContext && secretMenuContext.eligible
  return (
    <div className="redemption-list">
      <h3 className="redemption-list-title">
        {milestones.length === 1 ? 'Reward available' : 'Rewards available'}
      </h3>
      <ul className="redemption-list-items">
        {milestones.map((m) => (
          <li key={m.at_stamp} className="redemption-list-item">
            <div className="redemption-list-meta">
              <span className="redemption-list-position">
                Stamp {m.at_stamp}
              </span>
              {m.type === 'final' && (
                <span className="redemption-list-grand">Grand prize</span>
              )}
            </div>
            <div className="redemption-list-reward">{m.reward_text}</div>
            {m.type === 'final' && showSecretUnlocked && (
              <div className="redemption-list-secret-unlocked">
                <span className="redemption-secret-diamond" aria-hidden="true" />
                Secret menu reward unlocked — choose at redeem
              </div>
            )}
            {m.type === 'final' && !showSecretUnlocked && showSecretProgress && (
              <div className="redemption-list-secret-progress">
                {secretMenuContext!.secret_stamp_count}
                {secretMenuContext!.required_secret_stamps != null &&
                  `/${secretMenuContext!.required_secret_stamps}`}{' '}
                off-menu stamps banked
              </div>
            )}
            <button
              type="button"
              className="owner-btn owner-btn-primary redemption-list-action"
              onClick={() => onRedeem(m)}
              disabled={disabled}
            >
              Redeem
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface RedemptionConfirmModalProps {
  milestone: Milestone
  cardId: string
  branchId: string | null
  customerName?: string | undefined
  /** Secret menu — see RedeemableMilestonesListProps. Non-null + eligible on
   *  a final-milestone redemption unlocks the regular/secret reward choice
   *  below the reward text. Null/ineligible → UI is unchanged from before
   *  this feature existed. */
  secretMenuContext?: SecretMenuContext | null
  postRedemption: (
    cardId: string,
    body: {
      milestone_at_stamp: number
      branch_id: string
      idempotency_key: string
      use_secret_reward?: boolean
    },
  ) => Promise<unknown>
  errorMessageFor: (code: string) => string
  /** Called when the user dismisses the modal (cancel or after success). */
  onClose: () => void
  /**
   * Fired optimistically on Confirm tap, BEFORE the network resolves.
   * Used to keep in-flight UI in sync (e.g. drop the milestone from a
   * local picker list).
   */
  onRedeemed: (milestone: Milestone) => void
  /**
   * Fired AFTER the POST has actually resolved successfully. Awaited
   * before the modal closes, so callers can settle cache invalidations
   * here and the parent picker re-paints with fresh data.
   */
  onCommitted?: (milestone: Milestone) => void | Promise<void>
}

export function RedemptionConfirmModal({
  milestone,
  cardId,
  branchId,
  customerName,
  secretMenuContext,
  postRedemption,
  errorMessageFor,
  onClose,
  onRedeemed,
  onCommitted,
}: RedemptionConfirmModalProps) {
  const idempotencyKey = useRef<string>(`redemption:${newId()}`)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // Secret menu — the regular/secret choice, only meaningful when
  // showSecretChoice is true. Defaults to the regular grand prize.
  const [useSecretReward, setUseSecretReward] = useState(false)
  const showSecretChoice =
    milestone.type === 'final' && secretMenuContext?.eligible === true

  // Wait-for-server: slide locks-right + spinner while the POST is in
  // flight. On success: invalidate, toast, close. The parent's onCommitted
  // is awaited so any cache refetch settles before the picker re-paints,
  // killing the "just-redeemed reward still in picker" flash.
  function submit() {
    if (!branchId) {
      setError('Pick a branch first.')
      return
    }
    if (pending) return
    const m = milestone
    const reward =
      showSecretChoice && useSecretReward
        ? (secretMenuContext?.reward_text ?? m.reward_text)
        : m.reward_text
    setPending(true)
    setError(null)
    void (async () => {
      try {
        await postRedemption(cardId, {
          milestone_at_stamp: m.at_stamp,
          branch_id: branchId,
          idempotency_key: idempotencyKey.current,
          // Only sent when the choice is actually in play — an ineligible
          // or non-final redemption keeps posting the same body shape as
          // before this feature existed.
          ...(showSecretChoice ? { use_secret_reward: useSecretReward } : {}),
        })
        // Notify the parent picker BEFORE close so its local filter has
        // dropped this milestone by the time the modal unmounts.
        onRedeemed(m)
        // Then let the parent settle any cache invalidation it owns.
        await onCommitted?.(m)
        toast.success(`Redeemed · ${reward}`)
        onClose()
      } catch (err) {
        const code =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code: unknown }).code)
            : ''
        const msg = code ? errorMessageFor(code) : 'Redemption failed'
        toast.error(msg)
        setPending(false)
      }
    })()
  }

  const name = customerName?.trim() || 'Customer'

  return (
    <div
      className="stamp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="redemption-modal-title"
      onClick={() => {
        if (!pending) onClose()
      }}
    >
      <div className="stamp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stamp-modal-icon" aria-hidden="true">
          🎁
        </div>
        <h2 id="redemption-modal-title" className="stamp-modal-title">
          Redeem for {name}?
        </h2>
        {showSecretChoice ? (
          <div
            className="redemption-secret-choice"
            role="radiogroup"
            aria-label="Choose reward"
          >
            <button
              type="button"
              className={`redemption-secret-option${
                !useSecretReward ? ' is-selected' : ''
              }`}
              role="radio"
              aria-checked={!useSecretReward}
              onClick={() => setUseSecretReward(false)}
            >
              <span className="redemption-secret-option-label">
                Regular grand prize
              </span>
              <span className="redemption-secret-option-reward">
                {milestone.reward_text}
              </span>
            </button>
            <button
              type="button"
              className={`redemption-secret-option${
                useSecretReward ? ' is-selected' : ''
              }`}
              role="radio"
              aria-checked={useSecretReward}
              onClick={() => setUseSecretReward(true)}
            >
              <span className="redemption-secret-option-label">
                Secret menu reward
              </span>
              <span className="redemption-secret-option-reward">
                {secretMenuContext?.reward_text}
              </span>
            </button>
          </div>
        ) : (
          <p className="stamp-modal-reward">{milestone.reward_text}</p>
        )}
        {milestone.type === 'final' && (
          <p className="redemption-modal-hint">
            This is the grand prize — the card will be marked complete.
          </p>
        )}
        {error && <p className="redemption-modal-error">{error}</p>}
        <div className="redemption-modal-stack">
          <SlideToConfirm
            label="Slide to redeem"
            confirmingLabel="Redeeming…"
            onConfirm={submit}
            disabled={!branchId}
            pending={pending}
            tone="success"
          />
          <button
            type="button"
            className="owner-btn redemption-modal-cancel"
            onClick={onClose}
            disabled={pending}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
